/**
 * Review Queue — coordinator review surface for completed assignments.
 *
 * Per Ludvig's hookup plan (Story 7g):
 *   - Completed assignments: cohort_progress filtered status.tag === 'Completed'
 *   - Per-step metrics: cohort_step_metrics joined with visible_orientation_steps
 *     for durationMinutes
 *   - Attention flag: rushed = timeSpentSeconds < durationMinutes * 60 * 0.5
 *   - Approve: no explicit reducer — coordinator marks as reviewed in the UI
 *     (we persist this client-side via localStorage, scoped per-browser)
 *   - Send back: minetwin.reducers.sendBackAssignment({ assignmentId }) —
 *     marks the reviewed attempt SentBack and resets the assignment to
 *     NotStarted; the person's NEXT start opens a new attempt from the first
 *     stop. Earlier answers stay on record. A group member is also moved out
 *     of the group's room into a session of their own when the site has an
 *     agent to create one (#190, sendBack.ts).
 *   - Comprehension checks: visible_orientation_checks +
 *     visible_orientation_check_keys + cohort_check_answers, aggregated in
 *     reviewChecks.ts. Every try is a row, so the queue shows the whole path
 *     a trainee took through a question, not just the final answer.
 *
 * Data is fully reactive — no SQL workaround needed. When the agent advances
 * a step and writes cohort_step_metrics, the matching row's rushed-flag
 * computation re-runs. When sendBackAssignment fires, the row disappears
 * from this view (status no longer Completed).
 */

import { Icons, type IconSpec } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { PortalIcons } from '@/components/portalIcons';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useEffect, useMemo, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import { formatLegacyVersionTag } from '@/lib/orientationVersion';
import { toMillis } from '@/lib/toMillis';
import { pairingIdsOf, inCohortFilter } from '@/lib/cohortFilter';
import { CohortFilterSelect } from '@/components/CohortFilterSelect';
import { useCohortEntities } from '@/hooks/useCohortEntities';
import { useCheckReview } from '@/hooks/useCheckReview';
import { useCohortProgress } from '@/hooks/useCohortProgress';
import { useCohortStepMetrics } from '@/hooks/useCohortStepMetrics';
import { useGroupRuns } from '@/hooks/useGroupRuns';
import { useProjectAgents } from '@/hooks/useProjectAgents';
import { useOrientationStepOrder } from '@/hooks/useOrientationStepOrder';
import { useOrientationSteps } from '@/hooks/useOrientationSteps';
import { useOrientations } from '@/hooks/useOrientations';
import { useProjects } from '@/hooks/useProjects';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { useTweaks } from '@/hooks/useTweaks';
import {
  type AttemptReview,
  type CheckQuality,
  type OptionMark,
  type ReviewAnswer,
  type ReviewCheck,
  type ReviewCheckDef,
  checkQuality,
  reviewAttempt,
} from './reviewChecks';
import { groupMemberAssignmentIds, sendBackConfirmBody } from './sendBack';
import type { Site, Toast } from '@/data/types';

type ReviewQueueProps = { toast: (t: Omit<Toast, 'id'>) => void };

const REVIEWED_STORAGE_KEY = 'mtwin:review:reviewed';
/** Rushed if actual seconds spent on a step is < half the expected duration. */
const RUSHED_RATIO = 0.5;

type StepBreakdown = {
  stepId: string;
  stepName: string;
  expectedSeconds: number;
  actualSeconds: number | null;
  attemptNumber: number;
  rushed: boolean;
};

/** The run under review when no step metric says otherwise. */
const FIRST_ATTEMPT = 1;

type AssignmentReview = {
  assignmentId: bigint;
  email: string;
  orientationId: string;
  orientationName: string;
  projectId: string;
  versionTag: string;
  completedAtMs: number;
  totalSecondsRecorded: number;
  rushedCount: number;
  steps: StepBreakdown[];
  /**
   * null when the orientation carries no checks the run could reach, and when
   * the reviewed attempt recorded no answers at all: a completion that predates
   * the authoring of the checks has no score, and scoring it 0% would invent a
   * failure the module never wrote onto the attempt.
   */
  checkReview: AttemptReview | null;
};

type QualityRow = CheckQuality & { orientationName: string };

export function ReviewQueue({ toast }: ReviewQueueProps) {
  const dialogs = useDialogs();
  const minetwinState = useSpacetimeDB('minetwin');
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const currentProjectId = currentSite?.projectName ?? '';
  const membership = useMySiteMembership(currentProjectId);

  const { rows: progress, isReady: progressReady } = useCohortProgress();
  const { rows: metrics, isReady: metricsReady } = useCohortStepMetrics();
  const { byStepId: stepByStepId, isReady: stepsReady } = useOrientationSteps();
  // Orientations are org-wide; the review queue rolls up completions
  // across whatever orientations the user can see, regardless of which
  // site authored them.
  const { orientations } = useOrientations();
  const {
    checksByStepId,
    beatsByStepVersion,
    correctByCheckId,
    answersByAssignment,
    settingsByOrientationId,
  } = useCheckReview();
  const { stepsByOrientation } = useOrientationStepOrder();
  // Which completions ran as part of a group: send-back treats those
  // differently (the member leaves the group's room), and says so.
  const { runs: groupRuns } = useGroupRuns();
  const groupMemberIds = useMemo(() => groupMemberAssignmentIds(groupRuns), [groupRuns]);
  // ... and whether the site has an agent to create the member's new session
  // with (without one they stay in the group's room). Until the agent rows
  // have loaded, assume it does: that is the normal case.
  const { byProjectId: agentForProject, isReady: agentsReady } = useProjectAgents();

  const orientationNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orientations) m.set(o.orientationId, o.displayName);
    return m;
  }, [orientations]);

  const stepNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const [stepId, s] of stepByStepId) m.set(stepId, s.displayName);
    return m;
  }, [stepByStepId]);

  // Checks hang off steps and steps are reused across orientations, so the
  // orientation's check list is the step chain flattened in position order,
  // which is also the order they present in.
  const checksByOrientation = useMemo(() => {
    const m = new Map<string, ReviewCheckDef[]>();
    for (const [orientationId, steps] of stepsByOrientation) {
      const defs: ReviewCheckDef[] = [];
      for (const s of steps) defs.push(...(checksByStepId.get(s.stepId) ?? []));
      if (defs.length > 0) m.set(orientationId, defs);
    }
    return m;
  }, [stepsByOrientation, checksByStepId]);

  const metricsByAssignment = useMemo(() => {
    type MetricRow = (typeof metrics)[number];
    const m = new Map<bigint, MetricRow[]>();
    for (const row of metrics) {
      const list = m.get(row.assignmentId) ?? [];
      list.push(row);
      m.set(row.assignmentId, list);
    }
    return m;
  }, [metrics]);

  const [reviewed, setReviewed] = useState<Set<string>>(() => loadReviewed());
  useEffect(() => {
    persistReviewed(reviewed);
  }, [reviewed]);

  const [showReviewed, setShowReviewed] = useState(false);
  const [tab, setTab] = useState<'assignments' | 'questions'>('assignments');
  // Trainee filter: '' is everyone. Applies to the queue only; question
  // quality is a cohort-wide measure and one trainee's misses say nothing
  // about the question.
  const [userFilter, setUserFilter] = useState('');
  // reviewed is persisted to localStorage as JSON, so it stays keyed by the
  // stringified id (JSON.stringify throws on bigint). busyAssignmentId is
  // in-memory only, so it can hold the bigint id directly.
  const [busyAssignmentId, setBusyAssignmentId] = useState<bigint | null>(null);

  // Cohort filter (Phase 4, req 3) — applied to both the queue and the
  // question-quality rollup so the two halves agree on scope.
  const { cohorts } = useCohortEntities(currentProjectId || undefined);
  const [cohortFilter, setCohortFilter] = useState<bigint | null>(null);
  const cohortPairingIds = useMemo(
    () => pairingIdsOf(cohorts, cohortFilter),
    [cohorts, cohortFilter],
  );

  const completedAssignments = useMemo<AssignmentReview[]>(() => {
    const out: AssignmentReview[] = [];
    for (const a of progress) {
      if (a.status.tag !== 'Completed') continue;
      // Per Cloud Lead 2026-06-18: site-scoped surface — only show
      // completions for the topbar's currently-selected project.
      if (currentProjectId && a.projectId !== currentProjectId) continue;
      if (!inCohortFilter(a.cohortAssignmentId ?? null, cohortPairingIds)) continue;
      const ms = metricsByAssignment.get(a.assignmentId) ?? [];
      let totalSeconds = 0;
      let rushedCount = 0;
      const steps: StepBreakdown[] = ms.map((m) => {
        const step = stepByStepId.get(m.stepId);
        const expectedSeconds = (step?.durationMinutes ?? 0) * 60;
        const actualSeconds =
          typeof m.timeSpentSeconds === 'number' ? m.timeSpentSeconds : null;
        const rushed =
          actualSeconds !== null &&
          expectedSeconds > 0 &&
          actualSeconds < expectedSeconds * RUSHED_RATIO;
        if (actualSeconds !== null) totalSeconds += actualSeconds;
        if (rushed) rushedCount += 1;
        return {
          stepId: m.stepId,
          stepName: step?.displayName ?? m.stepId,
          expectedSeconds,
          actualSeconds,
          attemptNumber: m.attemptNumber,
          rushed,
        };
      });
      steps.sort((a, b) => a.stepName.localeCompare(b.stepName));

      const settings = settingsByOrientationId.get(a.orientationId);
      const versionTag = a.orientationVersion.tag;
      // The module's scoring denominator: a check authored past the end of this
      // version's script is never presented, so it is not a question the run got
      // wrong (assignments::orientation_checks).
      const checks = (checksByOrientation.get(a.orientationId) ?? []).filter(
        (c) => c.beat < (beatsByStepVersion.get(c.stepId)?.get(versionTag) ?? 0),
      );
      const answers = answersByAssignment.get(a.assignmentId.toString()) ?? [];
      const attemptNumber = steps.reduce(
        (n, s) => Math.max(n, s.attemptNumber),
        FIRST_ATTEMPT,
      );
      const attemptAnswered = answers.some((r) => r.attemptNumber === attemptNumber);

      out.push({
        assignmentId: a.assignmentId,
        email: a.email,
        orientationId: a.orientationId,
        orientationName:
          orientationNameById.get(a.orientationId) ?? a.orientationId,
        projectId: a.projectId,
        versionTag: a.orientationVersion.tag,
        completedAtMs: toMillis(a.completedAt),
        totalSecondsRecorded: totalSeconds,
        rushedCount,
        steps,
        checkReview:
          checks.length > 0 && attemptAnswered
            ? reviewAttempt({
                checks,
                correctByCheckId,
                answers,
                attemptNumber,
                passingScorePct: settings?.passingScorePct ?? null,
                autoReassignOnFail: settings?.autoReassignOnFail ?? false,
              })
            : null,
      });
    }
    out.sort((a, b) => b.completedAtMs - a.completedAtMs);
    return out;
  }, [
    progress,
    metricsByAssignment,
    stepByStepId,
    orientationNameById,
    currentProjectId,
    cohortPairingIds,
    checksByOrientation,
    beatsByStepVersion,
    settingsByOrientationId,
    correctByCheckId,
    answersByAssignment,
  ]);

  // Question quality spans every trainee who reached a check, in progress or
  // completed, not just the assignments sitting in the queue right now.
  const qualityRows = useMemo<QualityRow[]>(() => {
    const byOrientation = new Map<string, (ReviewAnswer & { assignmentKey: string })[]>();
    for (const a of progress) {
      if (currentProjectId && a.projectId !== currentProjectId) continue;
      if (!inCohortFilter(a.cohortAssignmentId ?? null, cohortPairingIds)) continue;
      const assignmentKey = a.assignmentId.toString();
      const rows = answersByAssignment.get(assignmentKey);
      if (!rows) continue;
      const list = byOrientation.get(a.orientationId) ?? [];
      for (const r of rows) list.push({ ...r, assignmentKey });
      byOrientation.set(a.orientationId, list);
    }

    const out: QualityRow[] = [];
    for (const [orientationId, answers] of byOrientation) {
      const checks = checksByOrientation.get(orientationId);
      if (!checks) continue;
      const orientationName = orientationNameById.get(orientationId) ?? orientationId;
      for (const q of checkQuality({ checks, correctByCheckId, answers })) {
        out.push({ ...q, orientationName });
      }
    }
    out.sort((a, b) => b.missRate - a.missRate || b.trainees - a.trainees);
    return out;
  }, [
    progress,
    currentProjectId,
    cohortPairingIds,
    answersByAssignment,
    checksByOrientation,
    correctByCheckId,
    orientationNameById,
  ]);

  const trainees = useMemo(
    () => [...new Set(completedAssignments.map((a) => a.email))].sort(),
    [completedAssignments],
  );
  const visibleAssignments = completedAssignments.filter(
    (a) =>
      (showReviewed || !reviewed.has(a.assignmentId.toString())) &&
      (userFilter === '' || a.email === userFilter),
  );

  const connOnline = minetwinState.isActive;
  // Deliberately NOT gated on the check views: they ship in their own
  // subscription batch because an org that has not taken the checks republish
  // does not have them, and that batch then never applies. Gating the page on
  // it would leave the whole queue on the loading placeholder forever.
  const isReady = progressReady && metricsReady && stepsReady;

  function toggleReviewed(assignmentId: bigint) {
    const key = assignmentId.toString();
    setReviewed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSendBack(a: AssignmentReview) {
    if (
      !(await dialogs.confirm({
        title: `Send back ${a.orientationName} for ${a.email}?`,
        body: sendBackConfirmBody({
          isGroupMember: groupMemberIds.has(a.assignmentId.toString()),
          projectHasAgent: !agentsReady || agentForProject(a.projectId) !== null,
        }),
        confirmLabel: 'Send back',
      }))
    ) {
      return;
    }
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }
    setBusyAssignmentId(a.assignmentId);
    try {
      conn.reducers.sendBackAssignment({ assignmentId: a.assignmentId });
      // Local reviewed flag is now irrelevant — the row will leave the queue
      // on next subscription update. Drop it from the reviewed set so we
      // don't carry stale entries around.
      setReviewed((prev) => {
        const next = new Set(prev);
        next.delete(a.assignmentId.toString());
        return next;
      });
      toast({
        title: 'Sent back',
        sub: `${a.email} · ${a.orientationName}`,
        kind: 'good',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({
        title: 'Send back failed',
        sub: msg.slice(0, 200),
        kind: 'bad',
      });
    } finally {
      setBusyAssignmentId(null);
    }
  }

  const pendingCount = completedAssignments.filter(
    (a) => !reviewed.has(a.assignmentId.toString()),
  ).length;
  const reviewedCount = completedAssignments.length - pendingCount;
  const flaggedQuestions = qualityRows.filter((r) => r.flagged).length;
  const failedCount = completedAssignments.filter(
    (a) => a.checkReview?.passed === false,
  ).length;

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Review Queue" />;
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Review Queue" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Review Queue</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {pendingCount} pending · {reviewedCount} reviewed
            {failedCount > 0 ? (
              <span style={{ color: 'var(--bad)' }}> · {failedCount} failed</span>
            ) : null}
            {currentSite ? <> · {currentSite.name}</> : null}
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="tabs" role="tablist" aria-label="Review queue view">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'assignments'}
              className={tab === 'assignments' ? 'on' : ''}
              onClick={() => setTab('assignments')}
            >
              Assignments{pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'questions'}
              className={tab === 'questions' ? 'on' : ''}
              onClick={() => setTab('questions')}
            >
              Question quality{flaggedQuestions > 0 ? ` (${flaggedQuestions})` : ''}
            </button>
          </div>
          <CohortFilterSelect
            cohorts={cohorts}
            value={cohortFilter}
            onChange={setCohortFilter}
          />
          {tab === 'assignments' && trainees.length > 0 ? (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: 'var(--muted)',
              }}
            >
              Trainee
              <select
                className="inp"
                style={{ fontSize: 12, padding: '2px 6px', maxWidth: 220 }}
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                aria-label="Filter by trainee"
              >
                <option value="">(all)</option>
                {trainees.map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tab === 'assignments' ? (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={showReviewed}
                onChange={(e) => setShowReviewed(e.target.checked)}
              />
              Show already-reviewed
            </label>
          ) : null}
        </div>
      </div>

      <div className="page-body">
        {!isReady ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 12.5,
            }}
          >
            Loading completed assignments…
          </div>
        ) : tab === 'questions' ? (
          <QuestionQualityCard rows={qualityRows} />
        ) : visibleAssignments.length === 0 ? (
          <div className="card">
            <div
              style={{
                padding: 22,
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 13,
              }}
            >
              {pendingCount === 0 && reviewedCount === 0
                ? 'No completed orientations yet. They will appear here once the agent marks them complete.'
                : userFilter !== ''
                  ? `Nothing for ${userFilter}${showReviewed ? '' : ' pending'}.`
                  : showReviewed
                  ? 'Nothing to show.'
                  : `All caught up — ${reviewedCount} reviewed. Tick "Show already-reviewed" to see them.`}
            </div>
          </div>
        ) : (
          <div className="flex-col gap-12">
            {visibleAssignments.map((a) => (
              <AssignmentCard
                key={a.assignmentId.toString()}
                assignment={a}
                stepNameById={stepNameById}
                isReviewed={reviewed.has(a.assignmentId.toString())}
                busy={busyAssignmentId === a.assignmentId}
                onToggleReviewed={() => toggleReviewed(a.assignmentId)}
                onSendBack={() => handleSendBack(a)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// -- Card --------------------------------------------------------------------

function AssignmentCard({
  assignment,
  stepNameById,
  isReviewed,
  busy,
  onToggleReviewed,
  onSendBack,
}: {
  assignment: AssignmentReview;
  stepNameById: ReadonlyMap<string, string>;
  isReviewed: boolean;
  busy: boolean;
  onToggleReviewed: () => void;
  onSendBack: () => void;
}) {
  const completedDate = assignment.completedAtMs
    ? new Date(assignment.completedAtMs).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';
  const review = assignment.checkReview;
  return (
    <div
      className="card"
      style={{
        opacity: isReviewed ? 0.7 : 1,
        borderLeft:
          review?.passed === false
            ? '3px solid var(--bad)'
            : assignment.rushedCount > 0
              ? '3px solid var(--warn, #E8C45C)'
              : '3px solid transparent',
      }}
    >
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>
            {assignment.email}{' '}
            <span className="text-muted" style={{ fontWeight: 500, fontSize: 13 }}>
              · {assignment.orientationName}
            </span>
          </h3>
          <div className="sub" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              Completed <b>{completedDate}</b>
            </span>
            {/* Single-length mode: only a legacy timed assignment has a length. */}
            {formatLegacyVersionTag(assignment.versionTag) ? (
              <>
                <span>·</span>
                <span>
                  Length <b>{formatLegacyVersionTag(assignment.versionTag)}</b>
                </span>
              </>
            ) : null}
            <span>·</span>
            <span>
              Total recorded{' '}
              <b>{formatDuration(assignment.totalSecondsRecorded)}</b>
            </span>
            {review && review.totalChecks > 0 ? (
              <>
                <span>·</span>
                <span>
                  Score <b>{review.scorePct}%</b>{' '}
                  <span className="text-muted">
                    ({review.correctChecks}/{review.totalChecks} correct
                    {review.requiredChecks !== null
                      ? `, ${review.requiredChecks} needed`
                      : ''}
                    )
                  </span>
                </span>
                {review.passed === false ? <span className="pill bad">Failed</span> : null}
                {review.passed === true ? <span className="pill good">Passed</span> : null}
                {review.autoReassigned ? (
                  <span className="pill warn">Auto re-assigned</span>
                ) : review.attemptNumber > 1 ? (
                  <span className="pill neutral">Attempt #{review.attemptNumber}</span>
                ) : null}
              </>
            ) : null}
            {assignment.rushedCount > 0 ? (
              <>
                <span>·</span>
                <span
                  style={{
                    background: 'var(--warn-soft, rgba(232,196,92,0.2))',
                    color: 'var(--warn, #E8C45C)',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {assignment.rushedCount} rushed step
                  {assignment.rushedCount === 1 ? '' : 's'}
                </span>
              </>
            ) : null}
            {isReviewed ? (
              <>
                <span>·</span>
                <span
                  style={{
                    background: 'var(--good-soft, rgba(92,200,154,0.2))',
                    color: 'var(--good, #5CC89A)',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Reviewed
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <button
            type="button"
            className="btn sm"
            onClick={onToggleReviewed}
            disabled={busy}
          >
            {isReviewed ? 'Unmark reviewed' : 'Mark reviewed'}
          </button>
          <button
            type="button"
            className="btn sm ghost"
            onClick={onSendBack}
            disabled={busy}
          >
            {busy ? 'Sending…' : 'Send back'}
          </button>
        </div>
      </div>

      {assignment.steps.length === 0 ? (
        <div
          style={{
            padding: 16,
            color: 'var(--muted)',
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          No step metrics recorded yet. (Step rows are written by the agent as
          it advances through the orientation.)
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Step</th>
              <th>Attempt</th>
              <th>Expected</th>
              <th>Actual</th>
              <th>Flag</th>
            </tr>
          </thead>
          <tbody>
            {assignment.steps.map((s) => (
              <tr
                key={`${s.stepId}-${s.attemptNumber}`}
                style={{
                  background: s.rushed
                    ? 'var(--warn-soft, rgba(232,196,92,0.08))'
                    : undefined,
                }}
              >
                <td>
                  <b>{s.stepName}</b>
                  <div style={{ color: 'var(--muted)', fontSize: 10.5 }}>
                    <code>{s.stepId}</code>
                  </div>
                </td>
                <td className="mono">#{s.attemptNumber}</td>
                <td className="mono">
                  {s.expectedSeconds > 0 ? formatDuration(s.expectedSeconds) : '—'}
                </td>
                <td className="mono">
                  {s.actualSeconds !== null
                    ? formatDuration(s.actualSeconds)
                    : '—'}
                </td>
                <td>
                  {s.rushed ? (
                    <span
                      style={{
                        color: 'var(--warn, #E8C45C)',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      Rushed
                    </span>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {review && review.totalChecks > 0 ? (
        <div style={{ padding: '12px 16px 14px', borderTop: '1px solid var(--border)' }}>
          <div
            style={{
              fontSize: 10.5,
              textTransform: 'uppercase',
              letterSpacing: '.1em',
              color: 'var(--muted)',
              marginBottom: 10,
            }}
          >
            Comprehension checks · attempt #{review.attemptNumber}
          </div>
          <div className="flex-col gap-14">
            {review.checks.map((c) => (
              <CheckTrail
                key={c.checkId}
                check={c}
                stepName={stepNameById.get(c.stepId) ?? c.stepId}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// -- Check trail -------------------------------------------------------------

/**
 * The full path a trainee took through one question. Per the spec the review
 * queue always shows the complete per-option breakdown, whatever the check's
 * `show_correct_answer` setting hid from the trainee at runtime.
 */
function CheckTrail({ check, stepName }: { check: ReviewCheck; stepName: string }) {
  const outcome =
    check.outcome === 'correct'
      ? { label: 'Correct', tone: 'good' }
      : check.outcome === 'failed'
        ? { label: 'Failed', tone: 'bad' }
        : { label: 'Not reached', tone: 'neutral' };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 12.5 }}>{check.question}</b>
        <span className={`pill plain ${outcome.tone}`}>{outcome.label}</span>
        {check.multiSelect ? <span className="pill plain neutral">Multi-select</span> : null}
        {check.grading === 'Any' ? <span className="pill plain neutral">Any of</span> : null}
        <span className="text-muted" style={{ fontSize: 11 }}>
          {stepName} · beat {check.beat}
        </span>
      </div>

      {check.tries.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 11.5, marginTop: 5 }}>
          The trainee never reached this check. It counts as wrong for the score.
        </div>
      ) : (
        <div className="flex-col gap-6" style={{ marginTop: 6 }}>
          {check.tries.map((t) => (
            <div
              key={t.tryNumber}
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
            >
              <span
                className="mono text-muted"
                style={{ fontSize: 11, minWidth: 40, paddingTop: 2 }}
              >
                Try {t.tryNumber}
              </span>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
                {t.expired ? <span className="pill plain warn">Timed out</span> : null}
                {t.options.map((o) => (
                  <OptionChip key={o.id} text={o.text} mark={o.mark} />
                ))}
              </div>
              <span className="mono text-muted" style={{ fontSize: 10.5, paddingTop: 3 }}>
                {t.answeredAtMs
                  ? new Date(t.answeredAtMs).toLocaleTimeString(undefined, {
                      timeStyle: 'medium',
                    })
                  : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OPTION_MARKS: Record<OptionMark, { glyph: IconSpec | null; tone: string; title: string }> = {
  correct: { glyph: Icons.status.success, tone: 'good', title: 'Selected, correct' },
  wrong: { glyph: Icons.status.error, tone: 'bad', title: 'Selected, wrong' },
  missed: { glyph: PortalIcons.status.answerUnpicked, tone: 'warn', title: 'Correct, not selected' },
  picked: { glyph: PortalIcons.status.answerPicked, tone: 'info', title: 'Selected (answer key not visible to this role)' },
  unpicked: { glyph: null, tone: 'neutral', title: 'Not selected' },
};

function OptionChip({ text, mark }: { text: string; mark: OptionMark }) {
  const m = OPTION_MARKS[mark];
  return (
    <span
      className={`pill plain ${m.tone}`}
      title={m.title}
      style={{ opacity: mark === 'unpicked' ? 0.5 : 1 }}
    >
      {m.glyph && <><Icon of={m.glyph} size={12} />{' '}</>}
      {text}
    </span>
  );
}

// -- Question quality --------------------------------------------------------

/**
 * Which questions the cohort keeps getting wrong. A high first-try miss rate,
 * or one distractor outdrawing the key, points at the question rather than at
 * the trainees: same answer rows, aggregated the other way round.
 */
function QuestionQualityCard({ rows }: { rows: QualityRow[] }) {
  const flagged = rows.filter((r) => r.flagged).length;
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>Question quality</h3>
          <div className="sub">
            First-try miss rate across trainees, in progress or completed ·{' '}
            {flagged > 0 ? (
              <span style={{ color: 'var(--warn)' }}>
                {flagged} question{flagged === 1 ? '' : 's'} over 50%
              </span>
            ) : (
              'nothing over 50%'
            )}
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 22, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          No comprehension-check answers recorded yet.
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Question</th>
              <th>Orientation</th>
              <th>Trainees</th>
              <th>First-try misses</th>
              <th>Most-picked wrong option</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.checkId}-${r.orientationName}`}
                style={{
                  background: r.flagged
                    ? 'var(--warn-soft, rgba(232,196,92,0.08))'
                    : undefined,
                }}
              >
                <td>
                  <b>{r.question}</b>
                  <div style={{ color: 'var(--muted)', fontSize: 10.5 }}>
                    <code>{r.checkId}</code>
                  </div>
                </td>
                <td className="text-muted">{r.orientationName}</td>
                <td className="mono">{r.trainees}</td>
                <td className="mono">
                  {r.firstTryWrong} ({Math.round(r.missRate * 100)}%)
                  {r.flagged ? <span className="pill warn" style={{ marginLeft: 6 }}>Review</span> : null}
                </td>
                <td>
                  {r.topWrongOption ? (
                    <>
                      {r.topWrongOption.text}{' '}
                      <span className="text-muted mono">{`×${r.topWrongOption.count}`}</span>
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// -- helpers ----------------------------------------------------------------

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return s === 0 ? `${m}m` : `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

function loadReviewed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(REVIEWED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((x) => typeof x === 'string'));
  } catch {
    /* fall through */
  }
  return new Set();
}

function persistReviewed(s: Set<string>): void {
  try {
    window.localStorage.setItem(REVIEWED_STORAGE_KEY, JSON.stringify([...s]));
  } catch {
    /* localStorage may be unavailable in some environments */
  }
}
